import React, { useState, useEffect, useRef } from 'react';
import { useSettings } from '../contexts/SettingsContext';
import SoundManager from '../components/effects/SoundManager';
import ParticleEffects from '../components/effects/ParticleEffects';
import LoginModal from '../components/LoginModal';
import AdminPanel from '../components/AdminPanel/AdminPanel';
import confetti from 'canvas-confetti';
import Tick from '@pqina/flip';
import '@pqina/flip/dist/flip.min.css';
import './CountdownDisplay.css';

const CountdownDisplay = () => {
  // loading kaldırıldı - artık yükleniyor ekranı asla gösterilmeyecek
  const { settings, isAuthenticated, isSettingsReady } = useSettings();
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [showLogin, setShowLogin] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [email, setEmail] = useState('');
  // Email durumu - localStorage'dan kontrol et (bir kere gönderilince kalıcı olarak gizlenir)
  const [emailStatus, setEmailStatus] = useState(() => {
    return localStorage.getItem('email_subscribed') === 'true' ? 'hidden' : '';
  });
  const soundManagerRef = useRef(null);
  const particleEffectsRef = useRef(null);
  const lastMilestone = useRef(null);
  const containerRef = useRef(null);
  const pageLoadTimeRef = useRef(Date.now()); // Sayfa yüklenme zamanı
  
  // Gizli admin erişim için
  const secretKeySequenceRef = useRef([]);
  const secretKeyTimeoutRef = useRef(null);
  const isSecretModeActiveRef = useRef(false); // Cmd+Shift+A sonrası aktif mi?
  
  // Kutlama aralığını settings'ten al (varsayılan 6 saat)
  const getCelebrationCooldown = () => {
    const hours = settings?.celebration_cooldown_hours || 6;
    return hours * 60 * 60 * 1000; // saat -> ms
  };
  
  // Son kutlama zamanını kontrol et
  const canCelebrate = () => {
    // Sayfa yüklendikten en az 3 saniye geçmeli (başlangıçta tetiklemeyi önle)
    if (Date.now() - pageLoadTimeRef.current < 3000) return false;
    
    const lastCelebration = localStorage.getItem('last_celebration_time');
    if (!lastCelebration) return true;
    
    const timeSince = Date.now() - parseInt(lastCelebration, 10);
    return timeSince >= getCelebrationCooldown();
  };
  
  const markCelebration = () => {
    localStorage.setItem('last_celebration_time', Date.now().toString());
  };

  // Email signup handler
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) return;
    if (emailStatus === 'subscribed') return; // Zaten kayıtlı
    
    setEmailStatus('loading');
    
    try {
      // LocalStorage'a kaydet
      const savedEmails = JSON.parse(localStorage.getItem('signup_emails') || '[]');
      if (!savedEmails.includes(email)) {
        savedEmails.push(email);
        localStorage.setItem('signup_emails', JSON.stringify(savedEmails));
      }
      
      // Kalıcı olarak kayıtlı işaretle
      localStorage.setItem('email_subscribed', 'true');
      setEmailStatus('subscribed');
      setEmail('');
      
      // 🎉 Epic Confetti Celebration
      const colors = [
        settings?.logo_color || '#e8dcc4', 
        settings?.label_color || '#a0a0a8',
        '#ffffff',
        '#ffd700', // gold
        '#ff6b6b'  // coral
      ];
      
      const defaults = {
        colors,
        disableForReducedMotion: true,
        zIndex: 9999
      };
      
      // Cannon burst - soldan
      confetti({
        ...defaults,
        particleCount: 50,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.6 },
        startVelocity: 45
      });
      
      // Cannon burst - sağdan
      confetti({
        ...defaults,
        particleCount: 50,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.6 },
        startVelocity: 45
      });
      
      // Üstten yağmur
      setTimeout(() => {
        confetti({
          ...defaults,
          particleCount: 100,
          spread: 120,
          origin: { y: 0, x: 0.5 },
          startVelocity: 30,
          gravity: 0.6,
          scalar: 1.2,
          drift: 0
        });
      }, 100);
      
      // Stars burst - ortadan
      setTimeout(() => {
        confetti({
          ...defaults,
          particleCount: 30,
          spread: 360,
          origin: { y: 0.35, x: 0.5 },
          startVelocity: 20,
          gravity: 0.4,
          scalar: 0.8,
          shapes: ['star'],
          ticks: 300
        });
      }, 200);
      
      // 2 saniye sonra formu tamamen gizle
      setTimeout(() => {
        setEmailStatus('hidden');
      }, 2000);
      
    } catch (err) {
      setEmailStatus('error');
      setTimeout(() => setEmailStatus(''), 3000);
    }
  };

  // Initialize managers
  useEffect(() => {
    soundManagerRef.current = new SoundManager();
    soundManagerRef.current.init();
    
    if (containerRef.current) {
      particleEffectsRef.current = new ParticleEffects(containerRef.current);
    }
    
    return () => {
      if (particleEffectsRef.current) {
        particleEffectsRef.current.stopAmbientParticles();
      }
    };
  }, []);

  // CSS değişkenlerini settings'e göre uygula
  useEffect(() => {
    if (!settings) return;
    
    const root = document.documentElement;
    const rootEl = document.getElementById('root');
    
    // Renkleri CSS değişkeni olarak uygula
    if (settings.card_color) {
      root.style.setProperty('--flip-card-color', settings.card_color);
    }
    if (settings.text_color) {
      root.style.setProperty('--flip-text-color', settings.text_color);
    }
    if (settings.background_color) {
      root.style.setProperty('--flip-bg-color', settings.background_color);
    }
    if (settings.split_line_color) {
      root.style.setProperty('--flip-split-color', settings.split_line_color);
    }
    if (settings.label_color) {
      root.style.setProperty('--flip-label-color', settings.label_color);
    }
    if (settings.logo_color) {
      root.style.setProperty('--flip-logo-color', settings.logo_color);
    }
  }, [settings?.card_color, settings?.text_color, settings?.background_color, settings?.split_line_color, settings?.label_color, settings?.logo_color]);

  // isSettingsReady olduğunda sayfayı göster (FOUC önleme)
  useEffect(() => {
    if (isSettingsReady) {
      const rootEl = document.getElementById('root');
      if (rootEl && !rootEl.classList.contains('ready')) {
        // Çok kısa gecikme - renklerin uygulanmasını bekle
        requestAnimationFrame(() => {
          rootEl.classList.add('ready');
        });
      }
    }
  }, [isSettingsReady]);

  // Ambient particles kontrolü - settings'e göre
  useEffect(() => {
    if (!particleEffectsRef.current || !settings) return;

    // Önce genel particle effects kontrolü - sadece false ise kapat
    const particleEffectsEnabled = settings.particle_effects_enabled !== false;
    
    if (!particleEffectsEnabled) {
      particleEffectsRef.current.stopAmbientParticles();
      return;
    }

    // Genel particle effects açıksa, ambient particles kontrolü yap
    const ambientEnabled = settings.ambient_particles_enabled !== false;
    const count = settings.ambient_particles_count || 25;

    if (ambientEnabled) {
      particleEffectsRef.current.startAmbientParticles(count);
    } else {
      particleEffectsRef.current.stopAmbientParticles();
    }

    return () => {
      if (particleEffectsRef.current) {
        particleEffectsRef.current.stopAmbientParticles();
      }
    };
  }, [settings?.particle_effects_enabled, settings?.ambient_particles_enabled, settings?.ambient_particles_count]);

  // Gizli admin erişim yöntemleri
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Yöntem 1: Klavye kısayolu - Ctrl+Shift+A (veya Cmd+Shift+A Mac'te)
      // Bu tuş kombinasyonu gizli modu aktif eder, sonra "kaplan" yazılmalı
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        // Gizli modu aktif et
        isSecretModeActiveRef.current = true;
        secretKeySequenceRef.current = []; // Diziyi sıfırla
        // 5 saniye sonra gizli modu kapat
        if (secretKeyTimeoutRef.current) {
          clearTimeout(secretKeyTimeoutRef.current);
        }
        secretKeyTimeoutRef.current = setTimeout(() => {
          isSecretModeActiveRef.current = false;
          secretKeySequenceRef.current = [];
        }, 5000);
        return;
      }

      // Yöntem 2: Gizli tuş dizisi - Cmd+Shift+A sonrasında "KAPLAN" yazmak (büyük/küçük harf duyarsız)
      // Önce Cmd+Shift+A'ya basılmalı, sonra "kaplan" yazılmalı
      if (isSecretModeActiveRef.current) {
        const key = e.key.toLowerCase();
        
        // Sadece harf tuşlarına bak
        if (key.length === 1 && /[a-z]/.test(key)) {
          // Harflerin input'lara yazılmasını engelle
          e.preventDefault();
          e.stopPropagation();
          
          secretKeySequenceRef.current.push(key);
          
          // Son 6 tuşa bak (kaplan = 6 harf)
          if (secretKeySequenceRef.current.length > 6) {
            secretKeySequenceRef.current.shift();
          }
          
          // Diziyi kontrol et
          if (secretKeySequenceRef.current.length === 6) {
            const sequence = secretKeySequenceRef.current.join('');
            if (sequence === 'kaplan') {
              // Gizli erişim bulundu!
              isSecretModeActiveRef.current = false;
              secretKeySequenceRef.current = [];
              if (secretKeyTimeoutRef.current) {
                clearTimeout(secretKeyTimeoutRef.current);
              }
              if (!isAuthenticated) {
                setShowLogin(true);
              } else {
                setShowAdmin(true);
              }
              return;
            }
          }
          
          // Timeout - 5 saniye içinde tuşlanmazsa sıfırla
          if (secretKeyTimeoutRef.current) {
            clearTimeout(secretKeyTimeoutRef.current);
          }
          secretKeyTimeoutRef.current = setTimeout(() => {
            isSecretModeActiveRef.current = false;
            secretKeySequenceRef.current = [];
          }, 5000);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (secretKeyTimeoutRef.current) {
        clearTimeout(secretKeyTimeoutRef.current);
      }
    };
  }, [isAuthenticated]);


  // Calculate remaining seconds
  useEffect(() => {
    if (!settings?.target_date) return;

    const calculateRemaining = () => {
      const now = new Date();
      const target = new Date(settings.target_date);
      const diff = Math.floor((target - now) / 1000);
      setRemainingSeconds(Math.max(0, diff));
    };

    calculateRemaining();
    const interval = setInterval(calculateRemaining, 1000);
    return () => clearInterval(interval);
  }, [settings?.target_date]);

  // Update sound settings
  useEffect(() => {
    if (soundManagerRef.current && settings) {
      soundManagerRef.current.setEnabled(settings.sound_enabled);
      soundManagerRef.current.setMasterVolume(settings.master_volume);
      soundManagerRef.current.setLayerEnabled('clack', settings.clack_enabled);
      soundManagerRef.current.setLayerEnabled('flutter', settings.flutter_enabled);
      soundManagerRef.current.setLayerEnabled('thud', settings.thud_enabled);
    }
  }, [
    settings?.sound_enabled,
    settings?.master_volume,
    settings?.clack_enabled,
    settings?.flutter_enabled,
    settings?.thud_enabled
  ]);

  // Milestone celebrations
  useEffect(() => {
    if (!settings || remainingSeconds <= 0) return;
    
    // Particle effects tamamen kapalıysa hiçbir şey yapma
    if (settings.particle_effects_enabled === false) return;
    
    // Milestone celebrations genel olarak kapalıysa hiçbir şey yapma
    if (settings.milestone_celebrations_enabled === false) return;

    const milestones = [
      { value: 1000000, enabled: settings.milestone_1m },
      { value: 500000, enabled: settings.milestone_500k },
      { value: 100000, enabled: settings.milestone_100k },
      { value: 10000, enabled: settings.milestone_10k },
      { value: 1000, enabled: settings.milestone_1k },
      { value: 100, enabled: settings.milestone_100 },
      { value: 10, enabled: settings.milestone_10 }
    ];

    const triggered = milestones.find(
      m => m.enabled && 
           remainingSeconds === m.value && 
           lastMilestone.current !== m.value
    );

    if (triggered) {
      lastMilestone.current = triggered.value;
      triggerCelebration();
    }
  }, [remainingSeconds, settings]);

  // Handle zero countdown with EPIC effects
  useEffect(() => {
    if (!settings) return;
    
    // Particle effects tamamen kapalıysa hiçbir şey yapma
    if (settings.particle_effects_enabled === false) return;
    
    // Milestone celebrations genel olarak kapalıysa hiçbir şey yapma
    if (settings.milestone_celebrations_enabled === false) return;
    
    if (remainingSeconds === 0 && settings.zero_countdown_effects_enabled !== false) {
      handleZeroEpic();
    } else if (remainingSeconds <= 10 && remainingSeconds > 0 && settings?.final_countdown_enabled && settings.final_countdown_effects_enabled !== false) {
      // Trigger effects for final 10 seconds
      if (remainingSeconds === 10) {
        particleEffectsRef.current?.triggerElectricSparks(50, 50, 15);
      }
      if (remainingSeconds === 5) {
        particleEffectsRef.current?.triggerGlowPulse(containerRef.current);
      }
    }
  }, [remainingSeconds, settings]);

  const triggerCelebration = () => {
    if (!settings) return;
    
    // Particle effects kapalıysa hiçbir şey yapma
    if (settings.particle_effects_enabled === false) return;
    
    // 6 saatte bir kontrol - başlangıçta veya son 6 saat içinde kutlama olduysa yapma
    if (!canCelebrate()) return;
    
    // Kutlama zamanını kaydet
    markCelebration();

    if (settings.celebration_type === 'confetti') {
      // ULTIMATE confetti + firework combo
      const duration = 4000;
      const animationEnd = Date.now() + duration;
      const defaults = { startVelocity: 35, spread: 360, ticks: 80, zIndex: 9999 };

      function randomInRange(min, max) {
        return Math.random() * (max - min) + min;
      }

      // Multi-burst confetti
      const interval = setInterval(function() {
        const timeLeft = animationEnd - Date.now();
        if (timeLeft <= 0) {
          return clearInterval(interval);
        }

        const particleCount = 60 * (timeLeft / duration);
        
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
          colors: ['#d97706', '#f59e0b', '#fbbf24', '#e8dcc4', '#ff6b6b', '#10b981']
        });
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
          colors: ['#d97706', '#f59e0b', '#fbbf24', '#e8dcc4', '#ff6b6b', '#10b981']
        });
      }, 250);
      
      // Add firework explosions
      setTimeout(() => particleEffectsRef.current?.triggerFirework(30, 40), 200);
      setTimeout(() => particleEffectsRef.current?.triggerFirework(70, 35), 500);
      setTimeout(() => particleEffectsRef.current?.triggerFirework(50, 30), 800);
      
    } else if (settings.celebration_type === 'flash') {
      // Enhanced flash with gradient + sparks
      const flash = document.createElement('div');
      flash.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: radial-gradient(circle at center, rgba(217, 119, 6, 0.9) 0%, transparent 70%);
        pointer-events: none;
        z-index: 9999;
        animation: flash-pulse 0.7s cubic-bezier(0.16, 1, 0.3, 1) forwards;
      `;
      document.body.appendChild(flash);
      setTimeout(() => flash.remove(), 700);
      
      // Electric sparks
      particleEffectsRef.current?.triggerElectricSparks(50, 50, 20);
    }
  };

  const handleZeroEpic = () => {
    if (!settings) return;
    
    // Particle effects kapalıysa hiçbir şey yapma
    if (settings.particle_effects_enabled === false) return;
    
    // 6 saatte bir kontrol
    if (!canCelebrate()) return;
    markCelebration();

    // MASSIVE FIREWORK EXPLOSION
    for (let i = 0; i < 5; i++) {
      setTimeout(() => {
        particleEffectsRef.current?.triggerFirework(
          Math.random() * 100,
          Math.random() * 60 + 20
        );
      }, i * 200);
    }

    // Screen shake
    if (containerRef.current) {
      containerRef.current.style.animation = 'screen-shake 0.5s cubic-bezier(0.36, 0.07, 0.19, 0.97)';
      setTimeout(() => {
        containerRef.current.style.animation = '';
      }, 500);
    }

    // Super confetti burst
    confetti({
      particleCount: 200,
      spread: 180,
      origin: { y: 0.5 },
      colors: ['#d97706', '#f59e0b', '#fbbf24', '#e8dcc4', '#ff6b6b', '#10b981'],
      startVelocity: 45,
      gravity: 1.2,
      scalar: 1.2
    });

    // Handle zero behavior
    setTimeout(() => {
      switch (settings.zero_behavior) {
        case 'message':
          console.log(settings.zero_message);
          break;
        case 'redirect':
          if (settings.zero_redirect_url) {
            window.location.href = settings.zero_redirect_url;
          }
          break;
        case 'restart':
          break;
        default:
          break;
      }
    }, 2000);
  };


  const handleLoginSuccess = () => {
    setShowLogin(false);
    setShowAdmin(true);
  };

  const soundCallbacks = {
    clack: () => soundManagerRef.current?.playClack(),
    flutter: () => soundManagerRef.current?.playFlutter(),
    thud: () => soundManagerRef.current?.playThud()
  };

  // Artık loading ekranı yok - her zaman anında render

  const isFinalCountdown = settings.final_countdown_enabled && 
                          remainingSeconds <= settings.final_countdown_threshold;

  return (
    <div 
      ref={containerRef}
      className={`countdown-display-container ${
        isFinalCountdown ? 'final-countdown' : ''
      }`}
      style={{
        backgroundColor: settings.background_color
      }}
    >
      {/* Logo - Üstte */}
      <div className="deeptho-logo">
        <span className="deeptho-text">Deeptho</span>
      </div>

      {/* Flip Clock - Ortada */}
      <div className="countdown-content">
        {settings.show_label && (
          <div className="countdown-label">
            {settings.label_text}
          </div>
        )}

        <div className="split-flap-wrapper flip-clock-wrapper">
          <FlipClockSeconds 
            remainingSeconds={remainingSeconds}
            settings={settings}
          />
        </div>
      </div>

      {/* Email Signup - Altta */}
      {emailStatus !== 'hidden' && (
        <div className={`email-signup ${emailStatus === 'subscribed' ? 'fade-out' : ''}`}>
          <form 
            onSubmit={handleEmailSubmit} 
            className="email-form"
          >
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email"
              className="email-input"
              disabled={emailStatus === 'loading' || emailStatus === 'subscribed'}
            />
            <button 
              type="submit" 
              className={`email-button ${emailStatus === 'loading' ? 'loading' : ''} ${emailStatus === 'subscribed' ? 'done' : ''}`}
              disabled={emailStatus === 'loading' || !email || emailStatus === 'subscribed'}
              aria-label="Subscribe"
            />
          </form>
        </div>
      )}

      {/* Modals */}
      {showLogin && !isAuthenticated && (
        <LoginModal
          onClose={() => setShowLogin(false)}
          onSuccess={handleLoginSuccess}
        />
      )}

      {(showAdmin && isAuthenticated) && (
        <AdminPanel onClose={() => setShowAdmin(false)} />
      )}
    </div>
  );
};

/**
 * FlipClockSeconds - Basit flip clock bileşeni
 */
const FlipClockSeconds = ({ remainingSeconds, settings }) => {
  const rootRef = React.useRef(null);
  const tickInstanceRef = React.useRef(null);
  const [isMounted, setIsMounted] = React.useState(false);

  // Saniyeyi formatla
  const formatSeconds = (totalSeconds) => {
    if (totalSeconds < 0) totalSeconds = 0;
    const digitCount = settings?.digit_count === 'auto' 
      ? Math.max(2, String(Math.floor(totalSeconds)).length)
      : parseInt(settings?.digit_count || 9);
    return String(Math.floor(totalSeconds)).padStart(digitCount, '0');
  };

  const displayValue = formatSeconds(remainingSeconds);

  // Mount kontrolü
  React.useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  // Tick instance oluştur
  React.useEffect(() => {
    if (!isMounted || !rootRef.current || tickInstanceRef.current) return;

    try {
      tickInstanceRef.current = Tick.DOM.create(rootRef.current, {
        value: displayValue
      });
    } catch (error) {
      console.error('FlipClock error:', error);
    }

    return () => {
      if (tickInstanceRef.current && rootRef.current) {
        try {
          Tick.DOM.destroy(rootRef.current);
        } catch (e) {}
        tickInstanceRef.current = null;
      }
    };
  }, [isMounted]);

  // Değer güncelle
  React.useEffect(() => {
    if (tickInstanceRef.current) {
      tickInstanceRef.current.value = displayValue;
    }
  }, [displayValue]);

  return (
    <div className="flip-clock-scale-wrapper">
      <div 
        ref={rootRef} 
        className="flip-clock-container flip-clock-seconds"
        aria-label={displayValue}
        role="timer"
      >
        <div className="tick" data-repeat="true" aria-hidden="true">
          <span data-view="flip"></span>
        </div>
      </div>
    </div>
  );
};

export default CountdownDisplay;